import { installSafeBtoa } from './safeBtoa';

(function() {
    'use strict';

    // empty-checkout and similar CMS templates do not load app.js / setup.js; they only load this
    // bundle (plus optional adyen.js). Install the UTF-8-safe btoa polyfill here so window.btoa
    // handles names outside Latin-1 (e.g. ẽ) before any checkout code runs.
    installSafeBtoa(window);

    // Global error handler for checkout forms
    window.CheckoutErrorHandler = {
        popup: null,

        init() {
            // Create the popup element dynamically
            this.createPopup();

            // Intercept fetch requests to checkout endpoints
            this.interceptFetch();
            this.interceptXHR();

            // Listen for custom error events
            document.addEventListener('checkout-error', (e) => {
                this.showError(e.detail.message, e.detail.errors);
            });
        },

        createPopup() {
            // Create popup container
            this.popup = document.createElement('div');
            this.popup.id = 'checkout-error-popup';
            this.popup.style.cssText = `
                position: fixed;
                bottom: 20px;
                right: 20px;
                max-width: 400px;
                background: #f8d7da;
                border: 1px solid #f5c6cb;
                border-radius: 8px;
                color: #721c24;
                padding: 16px 20px;
                font-size: 14px;
                line-height: 1.4;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                z-index: 9999;
                display: none;
                transform: translateX(100%);
                transition: transform 0.3s ease-in-out;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            `;

            // Create close button
            const closeBtn = document.createElement('button');
            closeBtn.innerHTML = '&times;';
            closeBtn.style.cssText = `
                position: absolute;
                top: 8px;
                right: 8px;
                background: none;
                border: none;
                font-size: 18px;
                cursor: pointer;
                color: #721c24;
                padding: 0;
                width: 20px;
                height: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                transition: background-color 0.2s ease;
            `;

            closeBtn.addEventListener('click', () => {
                this.hide();
            });

            closeBtn.addEventListener('mouseenter', () => {
                closeBtn.style.backgroundColor = 'rgba(114, 28, 36, 0.1)';
            });

            closeBtn.addEventListener('mouseleave', () => {
                closeBtn.style.backgroundColor = 'transparent';
            });

            // Create message container
            const messageEl = document.createElement('div');
            messageEl.className = 'checkout-error-message';
            messageEl.style.cssText = `
                margin: 0;
                padding-right: 20px;
            `;

            // Add elements to popup
            this.popup.appendChild(closeBtn);
            this.popup.appendChild(messageEl);

            // Add to document
            document.body.appendChild(this.popup);

            // Store reference to message element
            this.messageEl = messageEl;
        },

        showError(message, errors = null) {
            let displayMessage = message;

            if (errors) {
                if (Array.isArray(errors)) {
                    // Handle array-based errors (simple error messages)
                    displayMessage = errors.join('<br>');
                } else if (typeof errors === 'object') {
                    // Handle object-based errors (field-keyed validation errors)
                    const errorMessages = [];
                    Object.keys(errors).forEach(key => {
                        if (Array.isArray(errors[key])) {
                            errors[key].forEach(err => {
                                // Add field context to error messages
                                const fieldName = this.getFieldDisplayName(key);
                                errorMessages.push(`${fieldName}: ${err}`);
                            });
                        }
                    });

                    if (errorMessages.length > 0) {
                        displayMessage = errorMessages.join('<br>');
                    }
                }
            }

            this.messageEl.innerHTML = displayMessage;

            // Show popup with animation
            this.popup.style.display = 'block';

            // Trigger animation after a small delay to ensure display is set
            setTimeout(() => {
                this.popup.style.transform = 'translateX(0)';
            }, 10);

            // Auto-hide after 8 seconds
            setTimeout(() => this.hide(), 8000);
        },

        hide() {
            // Animate out
            this.popup.style.transform = 'translateX(100%)';

            // Hide after animation completes
            setTimeout(() => {
                this.popup.style.display = 'none';
            }, 300);
        },

        getFieldDisplayName(fieldKey) {
            const fieldMap = {
                // User fields
                'user.first_name': 'First Name',
                'user.last_name': 'Last Name',
                'user.email': 'Email',
                'user.phone': 'Phone',

                // Billing address fields
                'billing_address.first_name': 'Billing First Name',
                'billing_address.last_name': 'Billing Last Name',
                'billing_address.address': 'Billing Address',
                'billing_address.city': 'Billing City',
                'billing_address.state': 'Billing State',
                'billing_address.postal_code': 'Billing ZIP Code',
                'billing_address.country': 'Billing Country',

                // Shipping address fields (snake_case)
                'shipping_address.first_name': 'Shipping First Name',
                'shipping_address.last_name': 'Shipping Last Name',
                'shipping_address.address': 'Shipping Address',
                'shipping_address.city': 'Shipping City',
                'shipping_address.state': 'Shipping State',
                'shipping_address.postal_code': 'Shipping ZIP Code',
                'shipping_address.country': 'Shipping Country',

                // Shipping address fields (camelCase)
                'shippingAddress': 'Shipping Address',
                'shippingAddress.first_name': 'Shipping First Name',
                'shippingAddress.last_name': 'Shipping Last Name',
                'shippingAddress.address': 'Shipping Address',
                'shippingAddress.city': 'Shipping City',
                'shippingAddress.state': 'Shipping State',
                'shippingAddress.postal_code': 'Shipping ZIP Code',
                'shippingAddress.country': 'Shipping Country',

                // Payment fields
                'payment.token': 'Payment Method',
                'payment.gateway': 'Payment Gateway',
                'gateway': 'Payment Processing',

                // Other common fields
                'address': 'Address',
                'city': 'City',
                'state': 'State',
                'postal_code': 'ZIP Code',
                'country': 'Country',
                'phone': 'Phone Number',
                'email': 'Email Address'
            };

            // Check exact match first
            if (fieldMap[fieldKey]) {
                return fieldMap[fieldKey];
            }

            // Handle camelCase to readable text
            const camelCaseToReadable = fieldKey
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
                .trim();

            // Handle snake_case to readable text
            const snakeCaseToReadable = fieldKey
                .replace(/_/g, ' ') // Replace underscores with spaces
                .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word

            // Return the more readable version
            return camelCaseToReadable.length <= snakeCaseToReadable.length
                ? camelCaseToReadable
                : snakeCaseToReadable;
        },

        interceptFetch() {
            const originalFetch = window.fetch;
            window.fetch = async (...args) => {
                try {
                    const response = await originalFetch(...args);

                    // Check if this is a checkout API call
                    if (this.isCheckoutEndpoint(args[0])) {
                        if (!response.ok) {
                            const errorData = await response.clone().json().catch(() => ({}));
                            this.handleApiError(errorData, response.status);
                        }
                    }

                    return response;
                } catch (error) {
                    // AbortError is an intentional cancellation (e.g. Adyen SDK component teardown).
                    // Surfacing it as a user-visible error triggers DOM mutations that cause the SDK
                    // to call abort() on an already-aborted AbortController, producing:
                    //   InvalidAccessError: The object does not support the operation or argument.
                    // Silently re-throw so callers that care about cancellation can still handle it.
                    if (error && (error.name === 'AbortError')) {
                        throw error;
                    }
                    if (this.isCheckoutEndpoint(args[0])) {
                        this.showError('Network error occurred. Please check your connection and try again.');
                    }
                    throw error;
                }
            };
        },

        interceptXHR() {
            const originalOpen = XMLHttpRequest.prototype.open;
            const originalSend = XMLHttpRequest.prototype.send;

            XMLHttpRequest.prototype.open = function(method, url, ...args) {
                this._checkoutUrl = url;
                return originalOpen.call(this, method, url, ...args);
            };

            XMLHttpRequest.prototype.send = function(...args) {
                const xhr = this;

                xhr.addEventListener('load', function() {
                    if (xhr.status >= 400 && window.CheckoutErrorHandler.isCheckoutEndpoint(xhr._checkoutUrl)) {
                        try {
                            const errorData = JSON.parse(xhr.responseText);
                            window.CheckoutErrorHandler.handleApiError(errorData, xhr.status);
                        } catch (e) {
                            window.CheckoutErrorHandler.showError('An error occurred while processing your request.');
                        }
                    }
                });

                xhr.addEventListener('error', function() {
                    if (window.CheckoutErrorHandler.isCheckoutEndpoint(xhr._checkoutUrl)) {
                        window.CheckoutErrorHandler.showError('Network error occurred. Please check your connection and try again.');
                    }
                });

                return originalSend.call(this, ...args);
            };
        },

        isCheckoutEndpoint(url) {
            const checkoutEndpoints = [
                '/api/cart/pay',
                '/api/cart',
                '/api/cart/pre-auth',
                '/api/cart/payment-token',
                '/api/user/order'
            ];

            return checkoutEndpoints.some(endpoint =>
                url.includes(endpoint) || url.endsWith(endpoint)
            );
        },

        handleApiError(errorData, status) {
            if (errorData.type === 'error') {
                this.showError(errorData.message);
            } else if (errorData.errors) {
				this.showError('Please correct the following errors:', errorData.errors);
			} else if (status === 422) {
                this.showError('Please check your information and try again.');
            } else if (status >= 500) {
                this.showError('A server error occurred. Please try again later or contact support.');
            } else {
                this.showError('An unexpected error occurred. Please try again.');
            }
        }
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => window.CheckoutErrorHandler.init());
    } else {
        window.CheckoutErrorHandler.init();
    }
})();
